<template>
  <div class="editor-panel">
    <!-- <div class="editor-operate">
      <button @click="handleUndo" :disabled="!editorReady">撤回撤销</button>
      <button @click="handleInsertText" :disabled="!editorReady">光标处插入文本</button>
      <button @click="handleReplaceSelectText" :disabled="!editorReady">替换选中内容</button>
    </div> -->
    <div class="editor-body">
      <DocxEditor
        ref="editorRef"
        :i18n="zhCN"
        :showToolbar="true"
        :document-buffer="documentBuffer"
        :document="documentBuffer ? null : documentModel"
        :document-name="downloadName"
        @ready="onEditorReady"
        @change="onContentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from "vue";
import { DocxEditor, createEmptyDocument } from '@eigenpal/docx-editor-vue'
import { zhCN } from '@eigenpal/docx-editor-i18n'
import { provideLocale } from '@eigenpal/docx-editor-vue'
import { undo as pmUndo } from 'prosemirror-history'

provideLocale(zhCN);

const props = defineProps({
  modelValue: String,
  title: String,
  downloadName: String
});
const emit = defineEmits(["update:modelValue", "change"]);

const editorRef = ref();
// createEmptyDocument 返回 Document 对象，用 :document 而非 :document-buffer
const documentModel = ref(null);
// 用于直接加载二进制 docx 数据（如默认文档），优先级高于 documentModel
const documentBuffer = ref(null);
const editorValue = ref("");
let editorIns = null;
let readyResolve = null;
const editorReadyPromise = new Promise(res => readyResolve = res);
const editorReady = ref(false);
let editLock = false;

// 新增：防抖队列，解决连续多次apply叠加重叠
let insertTimer = null;
let pendingInsertText = "";

/**
 * 编辑器内容变更回调
 */
const onContentChange = () => {
  emit('change')
}

const onEditorReady = () => {
  console.log("=== onEditorReady 执行成功 ===");
  editorIns = editorRef.value;
  editorReady.value = true;
  readyResolve();
};

const getEditorView = () => {
  if (!editorIns) return null;
  const editorRefApi = editorIns.getEditorRef();
  if (!editorRefApi) return null;
  return editorRefApi.getView();
};

// 等待编辑器完全稳定，强制刷新文档基准
const waitEditorStable = async () => {
  await editorReadyPromise;
  await new Promise(resolve => setTimeout(resolve, 250));
  await nextTick();
  const view = getEditorView();
  if (!view) return null;
  // 强制聚焦，重置当前state文档基准
  view.focus();
  // 强制同步state，避免缓存旧文档快照
  view.state.doc;
  return view;
};

/**
 * 1. 撤销
 */
const undo = async () => {
  if (editLock) return;
  editLock = true;
  try {
    const view = await waitEditorStable();
    if (!view) return;
    pmUndo(view.state, view.dispatch);
  } catch (err) {
    console.error("撤销事务冲突", err);
  } finally {
    editLock = false;
  }
};

/**
 * 底层真正插入逻辑
 */
/**
 * 底层真正插入逻辑
 */
const doInsert = async (text) => {
  if (!text) return;
  if (editLock) {
    pendingInsertText = text;
    return;
  }
  editLock = true;
  pendingInsertText = "";
  try {
    const view = await waitEditorStable();
    if (!view) return;
    await new Promise(resolve => setTimeout(resolve, 280));
    const { state } = view;
    // 判空保护
    if (!state || !state.schema) return;
    const schema = state.schema;
    const { from } = state.selection;

    // 1. 预处理文本：清理重复叠字、多余空格，保留换行分段
    const cleanRaw = text
      .replace(/([\u4e00-\u9fa5])\1{5,}/g, "$1")
      .replace(/[ \t]{2,}/g, " ");
    const lines = cleanRaw.split(/\r?\n/);
    const fragmentContent = [];

    // 2. 逐行构建文本+标准换行节点
    lines.forEach((lineStr, idx) => {
      if (lineStr.trim()) {
        fragmentContent.push(schema.text(lineStr));
      }
      // 非最后一行插入换行硬节点
      if (idx !== lines.length - 1) {
        fragmentContent.push(schema.node("hardBreak"));
      }
    });

    // 修复：兼容标准Fragment导入，防止fromArray不存在
    const { Fragment } = await import('prosemirror-model');
    const contentFragment = Fragment.from(fragmentContent);
    // 单次事务插入完整片段
    const tr = state.tr.replaceWith(from, from, contentFragment);

    view.dispatch(tr);
    view.updateState(state.apply(tr));
    view.focus();
  } catch (err) {
    console.error("插入冲突重试", err);
    await new Promise(resolve => setTimeout(resolve, 400));
    const view = await waitEditorStable();
    if (!view) return;
    const { state } = view;
    if (!state || !state.schema) return;
    const schema = state.schema;
    const { from } = state.selection;

    // 重试分支同步文本清洗与Fragment处理
    const cleanRaw = text
      .replace(/([\u4e00-\u9fa5])\1{5,}/g, "$1")
      .replace(/[ \t]{2,}/g, " ");
    const lines = cleanRaw.split(/\r?\n/);
    const fragmentContent = [];
    lines.forEach((lineStr, idx) => {
      if (lineStr.trim()) fragmentContent.push(schema.text(lineStr));
      if (idx !== lines.length - 1) fragmentContent.push(schema.node("hardBreak"));
    });

    const { Fragment } = await import('prosemirror-model');
    const contentFragment = Fragment.from(fragmentContent);
    const tr = state.tr.replaceWith(from, from, contentFragment);

    view.dispatch(tr);
    view.updateState(state.apply(tr));
    view.focus();
  } finally {
    editLock = false;
    // 队列还有待插入文本，自动执行
    if (pendingInsertText) {
      const tmp = pendingInsertText;
      pendingInsertText = "";
      doInsert(tmp);
    }
  }
};

/**
 * 对外插入入口：防抖拦截连续多次调用
 */
const insertText = async (text) => {
  if (!text?.trim()) return;
  // 重置定时器，只执行最后一次文本
  pendingInsertText = text;
  clearTimeout(insertTimer);
  insertTimer = setTimeout(() => {
    doInsert(pendingInsertText);
  }, 180);
};

/**
 * 替换选中
 */
const replaceSelectText = async (newText) => {
  if (!newText?.trim() || editLock) return;
  editLock = true;
  try {
    const view = await waitEditorStable();
    if (!view) return;
    await new Promise(resolve => setTimeout(resolve, 120));
    const { state } = view;
    const { from, to } = state.selection;
    const tr = state.tr.replaceWith(from, to, state.schema.text(newText));
    view.dispatch(tr);
    view.focus();
  } catch (err) {
    console.error("替换冲突重试", err);
    await new Promise(resolve => setTimeout(resolve, 350));
    const view = await waitEditorStable();
    if (!view) return;
    const { state } = view;
    const { from, to } = state.selection;
    const tr = state.tr.replaceWith(from, to, state.schema.text(newText));
    view.dispatch(tr);
    view.focus();
  } finally {
    editLock = false;
  }
};

// let lastInsertTime = 0;
// const handleApply = async (text) => {
//   const now = Date.now();
//   // 最少间隔500ms才能执行下一次插入
//   if (now - lastInsertTime < 500) return;
//   lastInsertTime = now;
//   await insertText(text);
// };

// 加载默认文档（优先加载 智能文书生成中.docx，失败则创建空白文档）
onMounted(async () => {
  try {
    // const resp = await fetch('/智能文书生成.docx')
    // if (resp.ok) {
    //   const buffer = await resp.arrayBuffer()
    //   documentBuffer.value = new Uint8Array(buffer)
    // } else {
    //   documentModel.value = await createEmptyDocument()
    // }
    documentModel.value = await createEmptyDocument()
  } catch {
    documentModel.value = await createEmptyDocument()
  }
});

watch(() => props.modelValue, val => {
  editorValue.value = val;
}, { immediate: true });

watch(editorValue, val => {
  emit("update:modelValue", val);
});

/**
 * 从 URL 加载 docx 文件并显示
 */
const loadDocument = async (url) => {
  if (!url) return
  try {
    const response = await fetch(url)
    if (!response.ok) {
      console.error('加载文档失败：', response.status)
      return
    }
    const arrayBuffer = await response.arrayBuffer()
    // 编辑器准备好后，使用内部 loadDocumentBuffer 方法加载
    await editorReadyPromise
    if (editorRef.value?.loadDocumentBuffer) {
      await editorRef.value.loadDocumentBuffer(arrayBuffer)
    }
    console.log('文档已加载：', url)
  } catch (err) {
    console.error('加载文档异常：', err)
  }
}

/**
 * 保存并下载当前文档
 */
/**
 * 获取当前文档的 Blob（无需下载）
 */
const getBlob = async () => {
  await editorReadyPromise
  if (!editorRef.value?.save) return null
  try {
    const buffer = await editorRef.value.save()
    if (!buffer) return null
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
  } catch (err) {
    console.error('获取文档 Blob 失败：', err)
    return null
  }
}

const save = async (fileName = '文档.docx') => {
  await editorReadyPromise
  if (!editorRef.value?.save) return
  try {
    const buffer = await editorRef.value.save()
    if (!buffer) {
      console.error('保存文档失败：返回空数据')
      return
    }
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (err) {
    console.error('保存文档异常：', err)
  }
}

defineExpose({
  undo,
  insertText,
  replaceSelectText,
  editorReadyPromise,
  editorReady,
  loadDocument,
  getBlob,
  save
});
</script>

<style scoped lang="less">
.editor-panel {
  height:100%;
  display:flex;
  flex-direction:column;
  background:#fff;
  border-radius:14px;
  overflow:hidden;
  border:1px solid #E4EAF5;
}
.editor-operate {
  padding: 8px 16px;
  border-bottom: 1px solid #E4EAF5;
  display: flex;
  gap: 10px;
  button {
    padding: 6px 14px;
    background: #1677ff;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:disabled {
      background: #91caff;
      cursor: not-allowed;
    }
  }
}
.editor-body{
  flex:1;
  overflow:hidden;
  background:#F6F8FC;
}
</style>